'use client';

/**
 * AI Engines Slider — Swiper.js horizontal carousel
 * Used on /ai-stack page
 *
 * Setup: npm i swiper
 *
 * Features:
 *  - Side arrow buttons + bottom progress dots
 *  - Autoplay (5s) on desktop ≥901px, manual on mobile
 *  - Touch/swipe gestures (native Swiper)
 *  - Smooth fade + entrance animations per slide
 *  - Loop enabled, keyboard nav enabled
 *
 * All copy lives in AI_STACK data array — keep edits there.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Keyboard, A11y } from 'swiper/modules';
import { ChevronLeft, ChevronRight, ArrowRight, ArrowUpRight, Sparkles, Check } from 'lucide-react';
import 'swiper/css';
import { useTranslation } from "@/hooks/useTranslation";
const AI_STACK = [{
  slug: 'contextualpulse',
  name: 'ContextualPulse™',
  badge: 'Engine 01',
  image: '/aiengnes/conextualpulse.webp',
  question: 'How does your AI understand what customers actually mean?',
  desc: 'ContextualPulse™ goes beyond keywords. It reads emotion, decodes intent, and maps the mindset behind every message at scale.',
  bullets: ['Aspect-level sentiment analysis across tone, sarcasm, and embedded emotion', 'Audience segmentation by loyalty signal, buying intent, and mindset', 'Root-cause detection behind complaints, delight, or churn']
}, {
  slug: 'signalsense',
  name: 'SignalSense™',
  badge: 'Engine 02',
  image: '/aiengnes/signalsense.webp',
  question: 'How do you know a brand crisis is coming before it explodes?',
  desc: 'SignalSense™ monitors the signals most platforms miss — geo-tagged, intent-filtered, influencer-aware.',
  bullets: ['Detects chatter spikes and sentiment anomalies in real time', 'Geo-tagged brand monitoring with severity scoring and under 5-min alerts', 'Tracks emerging narratives across channels and markets before they go viral']
}, {
  slug: 'responsegenie',
  name: 'ResponseGenie™',
  badge: 'Engine 03',
  image: '/aiengnes/responsegenie.webp',
  question: 'What does AI-generated customer response look like at enterprise scale?',
  desc: 'ResponseGenie™ drafts replies that sound human, stay on-brand, and handle volume spikes without dropping quality.',
  bullets: ['Auto-generates brand-voice-consistent replies tuned to emotion and urgency', 'Pulls context from interaction history, knowledge base, and CRM', 'Human-in-the-loop approval — empathetic at scale, audit-ready by design']
}, {
  slug: 'agentiq',
  name: 'AgentIQ™',
  badge: 'Engine 04',
  image: '/aiengnes/agentiq.webp',
  question: 'Can AI actually make every agent better — not just the top ones?',
  desc: 'AgentIQ™ coaches in real time. It flags at-risk conversations, suggests interventions, and tracks the metrics that matter to your CX leadership.',
  bullets: ['Flags at-risk conversations and surfaces smart intervention prompts', 'Monitors tone, empathy, and compliance in real time', 'Improves CSAT, FRT, and AHT — the metrics your team is actually measured on']
}];
export default function AIEnginesSlider() {
  const {
    t
  } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(true);
  const swiperRef = useRef(null);

  // Detect viewport size for autoplay decision
  useEffect(() => {
    const checkSize = () => setIsDesktop(window.innerWidth >= 901);
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);
  const goToSlide = index => {
    if (swiperRef.current) {
      swiperRef.current.slideToLoop(index);
    }
  };
  const handlePrev = () => swiperRef.current?.slidePrev();
  const handleNext = () => swiperRef.current?.slideNext();
  return <section className={`aistk-engines ${!isDesktop ? 'aistk-engines--no-autoplay' : ''}`} id="engines">
      {/* Backgrounds */}
      <div className="aistk-engines__bg" aria-hidden="true" />
      <div className="aistk-engines__bg-overlay" aria-hidden="true" />

      <div className="container aistk-engines__inner">
        {/* Section header */}
        <div className="aistk-engines__header">
          <h2 className="aistk-engines__title">
            {t("aienginesslider.four")} <span className="aistk-engines__title-grad">{t("aienginesslider.ai_engines")}</span>
            <br />{t("aienginesslider.one_instinctive_cx_platform")}
          </h2>
          <p className="aistk-engines__sub">
            {t("aienginesslider.each_engine_is_purposebuilt_for_a_d")}
          </p>
        </div>

        {/* Slider */}
        <div className="aistk-engines__swiper-wrap">
          {/* Prev arrow */}
          <button className="aistk-engines__arrow aistk-engines__arrow--prev" onClick={handlePrev} aria-label={t("aienginesslider.previous_engine")} type="button">
            <ChevronLeft size={22} />
          </button>

          {/* Next arrow */}
          <button className="aistk-engines__arrow aistk-engines__arrow--next" onClick={handleNext} aria-label={t("aienginesslider.next_engine")} type="button">
            <ChevronRight size={22} />
          </button>

          <Swiper modules={[Autoplay, Keyboard, A11y]} className="aistk-engines-swiper" slidesPerView={1} spaceBetween={32} speed={700} loop={true} keyboard={{
          enabled: true
        }} a11y={{
          enabled: true
        }} autoplay={isDesktop ? {
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        } : false} onSwiper={swiper => {
          swiperRef.current = swiper;
        }} onSlideChange={swiper => setActiveIndex(swiper.realIndex)}>
            {AI_STACK.map((engine, i) => <SwiperSlide key={engine.slug}>
                <article className="aistk-engine-slide">
                  {/* Left — static engine illustration (from /public/aiengnes). */}
                  <div className="aistk-engine-slide__graphic">
                    <img
                      src={engine.image}
                      alt={`${engine.name} illustration`}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                      draggable={false}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>

                  {/* Right — content */}
                  <div className="aistk-engine-slide__content">
                    <div className="aistk-engine-slide__badge">
                      <span className="aistk-engine-slide__badge-icon">
                        <Sparkles size={10} />
                      </span>
                      {engine.badge}
                    </div>

                    <h3 className="aistk-engine-slide__name">
                      <a href={`/ai-stack/${engine.slug}`} className="aistk-engine-slide__name-link">
                        <span className="aistk-engine-slide__name-grad">{engine.name}</span>
                        <ArrowUpRight size={28} strokeWidth={2.4} className="aistk-engine-slide__name-icon" aria-hidden="true" />
                      </a>
                    </h3>

                    <p className="aistk-engine-slide__question">
                      {engine.question}
                    </p>

                    <p className="aistk-engine-slide__desc">
                      {engine.desc}
                    </p>

                    <ul className="aistk-engine-slide__bullets">
                      {engine.bullets.map((b, j) => <li key={j} className="aistk-engine-slide__bullet">
                          <span className="aistk-engine-slide__bullet-icon">
                            <Check size={12} strokeWidth={3} />
                          </span>
                          {b}
                        </li>)}
                    </ul>

                    <a href={`/ai-stack/${engine.slug}`} className="aistk-engine-slide__cta">
                      {t("aienginesslider.explore")} {engine.name} <ArrowRight size={15} />
                    </a>
                  </div>
                </article>
              </SwiperSlide>)}
          </Swiper>
        </div>

        {/* Progress dots */}
        <div className="aistk-engines__dots" role="tablist" aria-label={t("aienginesslider.ai_engines_navigation")}>
          {AI_STACK.map((engine, i) => <button key={engine.slug} className={`aistk-engines__dot ${activeIndex === i ? 'is-active' : ''}`} onClick={() => goToSlide(i)} role="tab" aria-selected={activeIndex === i} aria-label={`Go to ${engine.name}`} type="button" />)}
        </div>
      </div>
    </section>;
}